import { Typography as Text } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { ToolGroupEntry } from '../../extensions/tool-extension';
import { useToolExtensions } from '../../extensions/tool-extension';
import { ChatInformationBox } from '../ChatInformationBox';
import { formatDefaultToolLabel, inferToolIcon } from './defaultToolMappings';
import { useSharedToolStyles } from './sharedStyles';

const useStyles = makeStyles({
  text: {
    color: 'var(--text-subdued)',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--encore-spacing-tighter-2)',
  },
  listItem: {
    alignItems: 'center',
    display: 'flex',
    gap: 'var(--encore-spacing-tighter-2)',
    padding: 'var(--encore-spacing-tighter-4) 0',
  },
});

const ToolEntryLabel = ({
  entry,
  iconSize = 14,
}: {
  entry: ToolGroupEntry;
  iconSize?: number;
}) => {
  const shared = useSharedToolStyles();
  const classes = useStyles();
  const toolExtensions = useToolExtensions();
  const ext = toolExtensions.find(e => e.predicate(entry.tool));

  const IconComponent = ext?.icon ?? inferToolIcon(entry.tool);

  if (ext?.renderLabel) {
    return (
      <>
        <IconComponent size={iconSize} className={shared.icon} />
        {ext.renderLabel(entry)}
      </>
    );
  }

  const label = ext?.formatLabel
    ? ext.formatLabel(entry)
    : formatDefaultToolLabel(entry);

  return (
    <>
      <IconComponent size={iconSize} className={shared.icon} />
      <Text variant="body2" className={classes.text}>
        {label.text}
        {label.detail && (
          <span className={classes.text}>{` ${label.detail}`}</span>
        )}
      </Text>
    </>
  );
};

export const DefaultToolCallGroup = ({
  entries,
}: {
  entries: ToolGroupEntry[];
}) => {
  const classes = useStyles();
  const toolExtensions = useToolExtensions();

  if (entries.length === 1) {
    const entry = entries[0];
    const ext = toolExtensions.find(e => e.predicate(entry.tool));

    if (ext?.display === 'prominent' && ext.renderTool) {
      const renderedTool = ext.renderTool(entry);
      if (renderedTool) {
        return <>{renderedTool}</>;
      }
      return (
        <div className={classes.listItem}>
          <ToolEntryLabel entry={entry} iconSize={12} />
        </div>
      );
    }

    return <ChatInformationBox title={<ToolEntryLabel entry={entry} />} />;
  }

  return (
    <div className={classes.list}>
      {entries.map((entry, i) => {
        const ext = toolExtensions.find(e => e.predicate(entry.tool));

        if (ext?.display === 'prominent' && ext.renderTool) {
          const renderedTool = ext.renderTool(entry);
          if (!renderedTool) {
            return (
              <div key={i} className={classes.listItem}>
                <ToolEntryLabel entry={entry} iconSize={12} />
              </div>
            );
          }

          return <div key={i}>{renderedTool}</div>;
        }

        return (
          <div key={i} className={classes.listItem}>
            <ToolEntryLabel entry={entry} iconSize={12} />
          </div>
        );
      })}
    </div>
  );
};
